import { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type { TripChatExportFormat } from 'tripPlanner/api/v3'
import { useCreateTripChatExportMutation } from 'tripPlanner/hooks/api/chat'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { buildItineraryDownloadedActivityEntry } from './buildItineraryDownloadedActivityEntry'

interface UseTripChatItineraryExportArgs {
  chatId: number | undefined
  onExportCreated?: () => void
}

export function useTripChatItineraryExport({
  chatId,
  onExportCreated,
}: UseTripChatItineraryExportArgs) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBusOptional()
  const exportInFlightRef = useRef(false)
  const [loadingFormat, setLoadingFormat] = useState<TripChatExportFormat | undefined>(undefined)
  const { mutateAsync: createExport, isLoading } = useCreateTripChatExportMutation()

  const errorMessage = intl.formatMessage({
    id: 'h5ZSfq2gP8m3F-9GURmi',
    defaultMessage: 'Unable to download itinerary. Please try again.',
    description: 'Snackbar error message when trip planner itinerary export creation fails',
  })

  const downloadItinerary = useCallback(async(format: TripChatExportFormat) => {
    if (!chatId || isLoading || exportInFlightRef.current) return
    exportInFlightRef.current = true
    setLoadingFormat(format)

    try {
      const result = await createExport({ chatId, format })
      eventBus?.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildItineraryDownloadedActivityEntry(format)],
      })
      onExportCreated?.()
      // window.open(..., '_blank'), not window.location.assign -- downloadUrl can be either our
      // own API path or an external presigned URL, and nothing here can guarantee either responds
      // with Content-Disposition: attachment. Same-tab navigation to a URL that renders instead of
      // downloading (an expired-link page, a redirect that renders, anything without that header)
      // would unload this tab -- destroying the in-memory recentSurfaceActivity buffer
      // (useSurfaceActivityAccumulator's liveRef) along with the entry just dispatched above, and
      // every other entry queued from earlier in the turn, with nothing surfaced since the dispatch
      // itself already succeeded. Matches the existing pattern for presigned document URLs
      // elsewhere in tripPlanner (DocumentPdfView.tsx), which keeps the tab alive either way.
      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer')
      return result.downloadUrl
    } catch(error) {
      console.error('[TripChat] Failed to create itinerary export', error)
      showSnackbar(errorMessage, 'critical')
    } finally {
      exportInFlightRef.current = false
      setLoadingFormat(undefined)
    }
  }, [chatId, createExport, errorMessage, isLoading, onExportCreated, eventBus])

  const downloadPdf = useCallback(() => {
    return downloadItinerary('pdf')
  }, [downloadItinerary])

  const downloadSpreadsheet = useCallback(() => {
    return downloadItinerary('xlsx')
  }, [downloadItinerary])

  return {
    downloadPdf,
    downloadSpreadsheet,
    isExportLoading: isLoading,
    loadingFormat,
  }
}
